import { z } from 'zod'
import {
  UiConfigDiscountType,
  CouponType,
  AmountType,
  DiscountApplyToType,
  DiscountMethod,
  EntitledCatalogueSelectionMode
} from 'src/lib/types/discountsV2'
import { EntitledCatalogueSchema, positiveNumber, nonNegativeNumbersMinusOne, requiredString } from './common'
import { PrerequisiteSchema } from './prerequisites'
import { EntitledCatalogueType } from 'src/lib/types/discountsV2'

const refineCatalogueDuplicates = (
  catalogue: { entities: { entityId: string[] }[] }[],
  ctx: z.RefinementCtx,
  pathPrefix: string
) => {
  if (!catalogue) return

  const seenIds = new Map<string, { groupIndex: number; entityIndex: number }>()

  catalogue.forEach((group, groupIndex) => {
    if (!group?.entities) return
    group.entities.forEach((entity, entityIndex) => {
      if (!entity?.entityId) return
      entity.entityId.forEach(id => {
        if (seenIds.has(id)) {
          ctx.addIssue({
            code: 'custom',
            message: 'Duplicate product selected',
            path: [pathPrefix, groupIndex, 'entities', entityIndex]
          })
        } else {
          seenIds.set(id, { groupIndex, entityIndex })
        }
      })
    })
  })
}

const ConfigurationSchema = z.object({
  preview: z.object({
    header: requiredString.or(z.literal('')),
    description: requiredString.or(z.literal('')),
    longDescription: z.string().optional()
  })
})

const ApplicationMethodSchema = z
  .object({
    method: z.enum([DiscountMethod.CODE, DiscountMethod.AUTOMATIC]),
    discountCode: z.string().optional(),
    title: z.string().optional(),
    autoApplicable: z.boolean()
  })
  .superRefine((data, ctx) => {
    if (data.method === DiscountMethod.CODE) {
      if (!data.discountCode || data.discountCode.trim().length === 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'Discount code is required',
          path: ['discountCode']
        })
      }
    } else {
      if (!data.title || data.title.trim().length === 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'Title is required',
          path: ['title']
        })
      }
    }
  })

const BxgyEntitlementSchema = z.object({
  type: z.literal(CouponType.BXGY),
  amountType: z.literal(AmountType.PERCENTAGE).or(z.literal(AmountType.FLAT)),
  concessionAmount: positiveNumber,
  entitledCatalogue: EntitledCatalogueSchema,
  entitledProductCount: nonNegativeNumbersMinusOne,
  applicationMethod: ApplicationMethodSchema,
  applyTo: z.enum([
    DiscountApplyToType.ALL_PRODUCTS,
    DiscountApplyToType.SPECIFIC_PRODUCTS,
    DiscountApplyToType.SPECIFIC_COLLECTIONS
  ]),
  maxUsesPerOrder: z.number().min(1, 'Cannot be empty').nullable(),
  metaData: z
    .object({
      entitledCatalogue: z.array(z.any()),
      excludedCatalogue: z.array(z.any())
    })
    .optional()
})

const AmountOffOnProductsEntitlementSchema = z.object({
  type: z.literal(CouponType.AMOUNT),
  amountType: z.literal(AmountType.PERCENTAGE).or(z.literal(AmountType.FLAT)),
  concessionAmount: positiveNumber, // Can be 0 if free
  entitledCatalogue: EntitledCatalogueSchema,
  entitledProductCount: nonNegativeNumbersMinusOne,
  applicationMethod: ApplicationMethodSchema,
  applyTo: z.enum([
    DiscountApplyToType.ALL_PRODUCTS,
    DiscountApplyToType.SPECIFIC_PRODUCTS,
    DiscountApplyToType.SPECIFIC_COLLECTIONS
  ]),
  maxUsesPerOrder: z.number().min(1, 'Cannot be empty').nullable(),
  metaData: z
    .object({
      entitledCatalogue: z.array(z.any()),
      excludedCatalogue: z.array(z.any())
    })
    .optional()
})

const AmountOffEntitlementSchema = z.object({
  type: z.literal(CouponType.AMOUNT),
  amountType: z.enum([AmountType.PERCENTAGE, AmountType.FLAT]),
  concessionAmount: positiveNumber,
  maximumConcessionAmount: z.number().min(1).optional().nullable(),
  entitledProductCount: z.number().optional(),
  entitledCatalogue: EntitledCatalogueSchema.optional(),
  applicationMethod: ApplicationMethodSchema,
  applyTo: z.enum([
    DiscountApplyToType.ALL_PRODUCTS,
    DiscountApplyToType.SPECIFIC_PRODUCTS,
    DiscountApplyToType.SPECIFIC_COLLECTIONS
  ]),
  maxUsesPerOrder: z.number().min(1, 'Cannot be empty').nullable(),
  metaData: z
    .object({
      entitledCatalogue: z.array(z.any()),
      excludedCatalogue: z.array(z.any())
    })
    .optional()
})

const BaseStateSchema = z.object({
  prerequisite: PrerequisiteSchema,
  configuration: ConfigurationSchema
})

const BxgySchema = BaseStateSchema.extend({
  uiConfig: z.object({
    type: z.literal(UiConfigDiscountType.BXGY)
  }),
  entitlement: AmountOffOnProductsEntitlementSchema
}).superRefine((data, ctx) => {
  refineCatalogueDuplicates(data.entitlement.entitledCatalogue, ctx, 'entitlement.entitledCatalogue')

  if (data.entitlement.amountType === AmountType.PERCENTAGE && data.entitlement.concessionAmount > 100) {
    ctx.addIssue({
      code: 'custom',
      message: 'Percentage cannot exceed 100',
      path: ['entitlement', 'concessionAmount']
    })
  }

  if (!data.entitlement.entitledCatalogue || data.entitlement.entitledCatalogue.length === 0) {
    ctx.addIssue({
      code: 'custom',
      message: 'At least one product is required',
      path: ['entitlement', 'entitledCatalogue']
    })
  } else {
    data.entitlement.entitledCatalogue.forEach((group: EntitledCatalogueType, index: number) => {
      const isAllProducts =
        data.entitlement.metaData?.entitledCatalogue?.[index]?.[0]?.id === 'shopflo-all-products' ||
        data.entitlement.metaData?.entitledCatalogue?.[index]?.[0]?.id === 'shopflo-all-collections'

      if ((!group.entities || group.entities.length === 0) && !isAllProducts) {
        ctx.addIssue({
          code: 'custom',
          message: 'At least one product is required',
          path: ['entitlement', 'entitledCatalogue', index, 'entities']
        })
      }
    })
  }
})

const BxgyAutoAddedSchema = BaseStateSchema.extend({
  uiConfig: z.object({
    type: z.literal(UiConfigDiscountType.BXGY_AUTO_ADDED)
  }),
  entitlement: BxgyEntitlementSchema
}).superRefine((data, ctx) => {
  refineCatalogueDuplicates(data.entitlement.entitledCatalogue, ctx, 'entitlement.entitledCatalogue')

  if (data.entitlement.amountType === AmountType.PERCENTAGE && data.entitlement.concessionAmount > 100) {
    ctx.addIssue({
      code: 'custom',
      message: 'Percentage cannot exceed 100',
      path: ['entitlement', 'concessionAmount']
    })
  }

  if (!data.entitlement.entitledCatalogue || data.entitlement.entitledCatalogue.length === 0) {
    ctx.addIssue({
      code: 'custom',
      message: 'At least one product is required',
      path: ['entitlement', 'entitledCatalogue']
    })
  } else {
    data.entitlement.entitledCatalogue.forEach((group: EntitledCatalogueType, index: number) => {
      const isAllProducts =
        data.entitlement.metaData?.entitledCatalogue?.[index]?.[0]?.id === 'shopflo-all-products' ||
        data.entitlement.metaData?.entitledCatalogue?.[index]?.[0]?.id === 'shopflo-all-collections'

      if ((!group.entities || group.entities.length === 0) && !isAllProducts) {
        ctx.addIssue({
          code: 'custom',
          message: 'At least one product is required',
          path: ['entitlement', 'entitledCatalogue', index, 'entities']
        })
      }
    })
  }
})

const AmountOffSchema = BaseStateSchema.extend({
  uiConfig: z.object({
    type: z.enum([
      UiConfigDiscountType.AMOUNT_OFF,
      UiConfigDiscountType.AMOUNT_OFF_UPTO,
      UiConfigDiscountType.AMOUNT_OFF_PRODUCTS
    ])
  }),
  entitlement: AmountOffEntitlementSchema
}).superRefine((data, ctx) => {
  if (data.entitlement.entitledCatalogue) {
    refineCatalogueDuplicates(data.entitlement.entitledCatalogue, ctx, 'entitlement.entitledCatalogue')
  }

  if (data.entitlement.amountType === AmountType.PERCENTAGE && data.entitlement.concessionAmount > 100) {
    ctx.addIssue({
      code: 'custom',
      message: 'Percentage cannot exceed 100',
      path: ['entitlement', 'concessionAmount']
    })
  }

  // Validate Amount Off Products
  if (data.uiConfig.type === UiConfigDiscountType.AMOUNT_OFF_PRODUCTS) {
    if (!data.entitlement.entitledCatalogue || data.entitlement.entitledCatalogue.length === 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'Select at least one product',
        path: ['entitlement', 'entitledCatalogue']
      })
    } else {
      // Check if any group is empty
      data.entitlement.entitledCatalogue.forEach((group: EntitledCatalogueType, index: number) => {
        const isAllProducts =
          data.entitlement.metaData?.entitledCatalogue?.[index]?.[0]?.id === 'shopflo-all-products' ||
          data.entitlement.metaData?.entitledCatalogue?.[index]?.[0]?.id === 'shopflo-all-collections'

        if ((!group.entities || group.entities.length === 0) && !isAllProducts) {
          ctx.addIssue({
            code: 'custom',
            message: 'Group cannot be empty',
            path: ['entitlement', 'entitledCatalogue', index, 'entities']
          })
        }

        if (group.selectionMode === EntitledCatalogueSelectionMode.ANY) {
          if (!group.quantity || group.quantity <= 0) {
            ctx.addIssue({
              code: 'custom',
              message: 'Quantity must be at least 1',
              path: ['entitlement', 'entitledCatalogue', index, 'quantity']
            })
          }
        }
      })
    }
  }
})

const BundleSchema = BaseStateSchema.extend({
  uiConfig: z.object({
    type: z.literal(UiConfigDiscountType.BUNDLE)
  }),
  entitlement: z.object({
    type: z.literal(CouponType.BUNDLE),
    concessionAmount: z.number(),
    amountType: z.enum([AmountType.PERCENTAGE, AmountType.FLAT]),
    entitledCatalogue: EntitledCatalogueSchema,
    entitledProductCount: z.number().positive('Bundle size must be at least 1'),
    applicationMethod: ApplicationMethodSchema,
    applyTo: z.enum([
      DiscountApplyToType.ALL_PRODUCTS,
      DiscountApplyToType.SPECIFIC_PRODUCTS,
      DiscountApplyToType.SPECIFIC_COLLECTIONS
    ]),
    maxUsesPerOrder: z.number().min(1, 'Cannot be empty').nullable(),
    metaData: z
      .object({
        entitledCatalogue: z.array(z.any()),
        excludedCatalogue: z.array(z.any())
      })
      .optional()
  })
}).superRefine((data, ctx) => {
  refineCatalogueDuplicates(data.entitlement.entitledCatalogue, ctx, 'entitlement.entitledCatalogue')

  if (data.entitlement.concessionAmount <= 0) {
    const isPercentage = data.entitlement.amountType === AmountType.PERCENTAGE
    ctx.addIssue({
      code: 'custom',
      message: isPercentage ? 'Percentage must be greater than 0' : 'Price must be greater than 0',
      path: ['entitlement', 'concessionAmount']
    })
  }

  if (data.entitlement.applyTo !== DiscountApplyToType.ALL_PRODUCTS) {
    if (!data.entitlement.entitledCatalogue || data.entitlement.entitledCatalogue.length === 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'Select at least one product bundle',
        path: ['entitlement', 'entitledCatalogue']
      })
    } else {
      data.entitlement.entitledCatalogue.forEach((group: EntitledCatalogueType, index: number) => {
        const isAllProducts =
          data.entitlement.metaData?.entitledCatalogue?.[index]?.[0]?.id === 'shopflo-all-products' ||
          data.entitlement.metaData?.entitledCatalogue?.[index]?.[0]?.id === 'shopflo-all-collections'

        if ((!group.entities || group.entities.length === 0) && !isAllProducts) {
          ctx.addIssue({
            code: 'custom',
            message: 'Bundle group cannot be empty',
            path: ['entitlement', 'entitledCatalogue', index, 'entities']
          })
        }
      })
    }
  }
})

const ShippingSchema = BaseStateSchema.extend({
  uiConfig: z.object({
    type: z.literal(UiConfigDiscountType.SHIPPING)
  }),
  entitlement: z.object({
    type: z.literal(CouponType.SHIPPING),
    concessionAmount: positiveNumber,
    amountType: z.literal(AmountType.PERCENTAGE).or(z.literal(AmountType.FLAT)),
    applicationMethod: ApplicationMethodSchema
  })
})

const FreebieSchema = BaseStateSchema.extend({
  uiConfig: z.object({
    type: z.enum([UiConfigDiscountType.FREEBIE, UiConfigDiscountType.FREEBIE_SELECTOR])
  }),
  entitlement: BxgyEntitlementSchema
}).superRefine((data, ctx) => {
  refineCatalogueDuplicates(data.entitlement.entitledCatalogue, ctx, 'entitlement.entitledCatalogue')

  if (!data.entitlement.entitledCatalogue || data.entitlement.entitledCatalogue.length === 0) {
    ctx.addIssue({
      code: 'custom',
      message: 'Select at least one product',
      path: ['entitlement', 'entitledCatalogue']
    })
  } else {
    data.entitlement.entitledCatalogue.forEach((group: EntitledCatalogueType, index: number) => {
      const isAllProducts =
        data.entitlement.metaData?.entitledCatalogue?.[index]?.[0]?.id === 'shopflo-all-products' ||
        data.entitlement.metaData?.entitledCatalogue?.[index]?.[0]?.id === 'shopflo-all-collections'

      if ((!group.entities || group.entities.length === 0) && !isAllProducts) {
        ctx.addIssue({
          code: 'custom',
          message: 'Group cannot be empty',
          path: ['entitlement', 'entitledCatalogue', index, 'entities']
        })
      }
    })
  }
})

const ExitCouponSchema = BaseStateSchema.extend({
  uiConfig: z.object({
    type: z.literal(UiConfigDiscountType.EXIT_COUPON)
  }),
  entitlement: AmountOffEntitlementSchema
}).superRefine((data, ctx) => {
  if (data.entitlement.amountType === AmountType.PERCENTAGE && data.entitlement.concessionAmount > 100) {
    ctx.addIssue({
      code: 'custom',
      message: 'Percentage cannot exceed 100',
      path: ['entitlement', 'concessionAmount']
    })
  }
})

export const DiscountFormSchema = z.union([
  BxgySchema,
  BxgyAutoAddedSchema,
  AmountOffSchema,
  BundleSchema,
  ShippingSchema,
  FreebieSchema,
  ExitCouponSchema
])

export const getDiscountSchema = (type: UiConfigDiscountType) => {
  switch (type) {
    case UiConfigDiscountType.BXGY:
      return BxgySchema
    case UiConfigDiscountType.BXGY_AUTO_ADDED:
      return BxgyAutoAddedSchema
    case UiConfigDiscountType.AMOUNT_OFF:
    case UiConfigDiscountType.AMOUNT_OFF_UPTO:
    case UiConfigDiscountType.AMOUNT_OFF_PRODUCTS:
      return AmountOffSchema
    case UiConfigDiscountType.BUNDLE:
      return BundleSchema
    case UiConfigDiscountType.SHIPPING:
      return ShippingSchema
    case UiConfigDiscountType.FREEBIE:
    case UiConfigDiscountType.FREEBIE_SELECTOR:
      return FreebieSchema
    case UiConfigDiscountType.EXIT_COUPON:
      return ExitCouponSchema
    default:
      return DiscountFormSchema
  }
}
